import {
    useApplicationInfrastructureListQuery,
    useCreateApplicationInfrastructureMutation,
    useUpdateApplicationInfrastructureMutation,
} from './useApplicationInfrastructureQuery';
import {buildRegisterInput} from '../services/ApplicationInfrastructureService';
import {
    ApplicationInfrastructure,
    RegisterInfrastructureFormValues,
    UpdateApplicationInfrastructureInput,
} from '../types-infrastructure';

// FNP-1016: the infrastructure inventory for one application.
//
// The list is newest-first and append-only per provisioning act, so `record` is
// the CURRENT inventory: the first item. An application with no record at all is
// the registration case — the seven legacy production applications are all in
// exactly that state.

export const useApplicationInfrastructure = (applicationId?: string) => {

    const {
        data,
        isLoading: loading,
        refetch: fetchRecords,
    } = useApplicationInfrastructureListQuery(applicationId);

    const createMutation = useCreateApplicationInfrastructureMutation();
    const updateMutation = useUpdateApplicationInfrastructureMutation(applicationId);

    const records: ApplicationInfrastructure[] = data?.items ?? [];

    // Newest first, per the query contract.
    const record: ApplicationInfrastructure | undefined = records[0];

    // ⚠️ Only meaningful once the query has SETTLED. While loading, `records`
    // is empty and this would read `true`, offering to register infrastructure
    // that may already be recorded — a duplicate row. Callers must gate on
    // `loading` before acting on it, and the section component does.
    const hasInventory = records.length > 0;

    const registerRecord = async (values: RegisterInfrastructureFormValues) => {
        if (typeof applicationId === 'undefined') {
            throw new Error('applicationId is required parameter');
        }
        return await createMutation.mutateAsync(buildRegisterInput(applicationId, values));
    };

    const updateRecord = async (input: UpdateApplicationInfrastructureInput) => {
        return await updateMutation.mutateAsync(input);
    };

    return {
        record,
        records,
        hasInventory,
        loading,
        fetchRecords,
        registerRecord,
        updateRecord,
        registering: createMutation.isPending,
        updating: updateMutation.isPending,
    };
};
